// Preset and fixed-price snapping (SPEC §2.3 "Presets" and "Fixed prices"; D-P3-09, D-R8, D-R28,
// D-R32, D-R33). Everything is BigInt minor units of the display currency; the charged USD always
// derives from the snapped display figure.
import {
  displayMinorToUsdCents,
  divRound,
  fromMinorUnits,
  minorUnits,
  parseRate,
  toMinorUnits,
  usdCentsToDisplayMinor,
} from './convert.js';

// The ladder's mantissas {1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8}, in tenths of a decade's
// first rung: rung = mantissa × 10^k whole units, for k ≥ 0 (10, 12, 15 … 80, 100, 120 …).
// snapUnit's copy is mirrored by contracts/schemas display-pricing.ts: change both or neither.
const MANTISSAS = [10n, 12n, 15n, 20n, 25n, 30n, 40n, 50n, 60n, 75n, 80n];

// Mode 'up' (presets, defaults and giving levels; CON-744, D-R54): the same ladder plus 3.5, 4.5,
// 7 and 9, so a preset rounds up by less (EUR $50 → €45, not €50). Nothing on the server reads it.
const PRESET_MANTISSAS = [10n, 12n, 15n, 20n, 25n, 30n, 35n, 40n, 45n, 50n, 60n, 70n, 75n, 80n, 90n];

// The candidate set of one currency: below 10, whole units 1–9 (D-R28), or 0.1–9.9 in a
// 3-decimal currency (D-R33); from 10 up, the ladder. Computed arithmetically, so it is unbounded.
function candidatesFor(currency, mantissas = MANTISSAS) {
  const decimals = minorUnits(currency);
  const unit = 10n ** BigInt(decimals);
  const smallStep = decimals === 3 ? unit / 10n : unit;
  const ten = 10n * unit;

  function atOrAbove(value) {
    if (value <= smallStep) return smallStep;
    if (value < ten) {
      const stepped = divRound(value, smallStep, 'up') * smallStep;
      return stepped < ten ? stepped : ten;
    }
    for (let scale = unit; ; scale *= 10n) {
      for (const mantissa of mantissas) {
        if (mantissa * scale >= value) return mantissa * scale;
      }
    }
  }

  function atOrBelow(value) {
    if (value < smallStep) return null;
    if (value < ten) return divRound(value, smallStep, 'down') * smallStep;
    let scale = unit;
    while (100n * scale <= value) scale *= 10n;
    let best = null;
    for (const mantissa of mantissas) {
      if (mantissa * scale <= value) best = mantissa * scale;
    }
    return best;
  }

  return {
    unit,
    atOrAbove,
    atOrBelow,
    next: (value) => atOrAbove(value + 1n),
    previous: (value) => atOrBelow(value - 1n),
  };
}

// Nearest candidate by minor-unit distance, up on a tie; below the smallest, the smallest.
function nearest(ladder, value) {
  const low = ladder.atOrBelow(value);
  const high = ladder.atOrAbove(value);
  if (low === null || low === high) return high;
  return value - low < high - value ? low : high;
}

// A USD bound in cents, or null when it is missing, '' or unreadable (that clamp is skipped).
function readBound(bound) {
  if (bound === null || bound === undefined || bound === '') return null;
  try {
    return toMinorUnits(typeof bound === 'string' ? bound.trim() : bound, 'USD');
  } catch {
    return null;
  }
}

// A preset slot in USD cents, or null for an empty slot: '', '0', 0, null, undefined,
// whitespace, a non-positive value, or one that does not read as an amount.
function readSlot(slot) {
  if (slot === null || slot === undefined) return null;
  const text = typeof slot === 'string' ? slot.trim() : slot;
  if (text === '') return null;
  try {
    const cents = toMinorUnits(text, 'USD');
    return cents > 0n ? cents : null;
  } catch {
    return null;
  }
}

const EMPTY = { display: '', usd: '' };

/**
 * Converts USD preset slots into the display currency and snaps them (SPEC §2.3). Returns one
 * entry per input slot, in order, each carrying its `sourceIndex`: empty slots and dropped
 * duplicates stay in place with empty strings.
 */
export function snapPresets(slots, fxRate, currency, bounds = {}) {
  const rateMicro = parseRate(fxRate);
  const mode = bounds.mode ?? 'nearest';
  if (mode !== 'nearest' && mode !== 'ceil' && mode !== 'up')
    throw new RangeError(`unknown mode: ${String(mode)}`);
  // Mode 'up' never charges below the preset's USD: the conversion itself rounds up, so any rung
  // at or above it converts back to at least the preset.
  const roundsUp = mode === 'up';
  const minCents = readBound(bounds.minUsd);
  const maxCents = readBound(bounds.maxUsd);
  const ladder = candidatesFor(currency, roundsUp ? PRESET_MANTISSAS : MANTISSAS);
  const usdOf = (displayMinor) => displayMinorToUsdCents(displayMinor, rateMicro, currency);
  const aboveMin = (displayMinor) => minCents === null || usdOf(displayMinor) >= minCents;
  const belowMax = (displayMinor) => maxCents === null || usdOf(displayMinor) <= maxCents;
  const fits = (displayMinor) =>
    displayMinor !== null && aboveMin(displayMinor) && belowMax(displayMinor);

  function clamp(value) {
    let v = value;
    while (v !== null && !aboveMin(v)) v = ladder.next(v);
    while (v !== null && !belowMax(v)) v = ladder.previous(v);
    return fits(v) ? v : null;
  }

  // D-R8: no rung fits between min and max. Keep the converted value at whole units (half-up),
  // moved up when that breaks the min or down when it breaks the max; if no whole unit fits,
  // the same at minor units.
  function keepConverted(converted) {
    const steps = ladder.unit === 1n ? [1n] : [ladder.unit, 1n];
    for (const step of steps) {
      let v = divRound(converted > 0n ? converted : 0n, step, roundsUp ? 'up' : 'half-up') * step;
      if (v < step) v = step;
      if (!aboveMin(v)) {
        v =
          divRound(usdCentsToDisplayMinor(minCents, rateMicro, currency, 'down'), step, 'down') *
          step;
        while (!aboveMin(v)) v += step;
      }
      if (!belowMax(v)) {
        v =
          divRound(usdCentsToDisplayMinor(maxCents, rateMicro, currency, 'up'), step, 'up') * step;
        while (v > 0n && !belowMax(v)) v -= step;
      }
      if (v >= step && fits(v)) return v;
    }
    return null;
  }

  const kept = new Set();
  return Array.from(slots, (slot, sourceIndex) => {
    const cents = readSlot(slot);
    if (cents === null) return { ...EMPTY, sourceIndex, kind: 'empty' };
    const converted = usdCentsToDisplayMinor(cents, rateMicro, currency, roundsUp ? 'up' : 'half-up');
    const snapped = mode === 'nearest' ? nearest(ladder, converted) : ladder.atOrAbove(converted);
    let value = clamp(snapped) ?? keepConverted(converted);
    // Mode 'up' never steps below the conversion to fit the max: the max can only cap it, at the
    // conversion itself or the largest figure whose USD is within the max (review F2).
    if (roundsUp && value !== null && value < converted && maxCents !== null) {
      let cap = usdCentsToDisplayMinor(maxCents, rateMicro, currency, 'down');
      while (cap > 0n && !belowMax(cap)) cap -= 1n;
      const capped = converted <= cap ? converted : cap;
      if (capped > value && fits(capped)) value = capped;
    }
    if (value !== null && kept.has(value)) {
      let bumped = ladder.next(value);
      while (kept.has(bumped)) bumped = ladder.next(bumped);
      value = belowMax(bumped) ? bumped : null;
    }
    if (value === null) return { ...EMPTY, sourceIndex, kind: 'dropped' };
    kept.add(value);
    return {
      display: fromMinorUnits(value, currency),
      usd: fromMinorUnits(usdOf(value), 'USD'),
      sourceIndex,
      kind: 'preset',
    };
  });
}

/**
 * A fixed price (product, ticket) in the display currency (D-R32, D-R33): the lowest candidate at
 * or above the converted price; no clamp, no collisions. Its `usd` is max(toUsd(display), usd),
 * so a rounding edge never charges below the stored USD. A line total is quantity × this unit.
 */
export function snapUnit(usd, fxRate, currency) {
  const rateMicro = parseRate(fxRate);
  const cents = toMinorUnits(usd, 'USD');
  if (cents < 0n) throw new RangeError('a fixed price cannot be negative');
  if (cents === 0n)
    return { display: fromMinorUnits(0n, currency), usd: fromMinorUnits(0n, 'USD') };
  const converted = usdCentsToDisplayMinor(cents, rateMicro, currency);
  const display = candidatesFor(currency).atOrAbove(converted);
  const derived = displayMinorToUsdCents(display, rateMicro, currency);
  return {
    display: fromMinorUnits(display, currency),
    usd: fromMinorUnits(derived > cents ? derived : cents, 'USD'),
  };
}
